import RateDisplay from "@components/Cards/RateDisplay";
import { useFormatContext } from "@lib/hooks/format-context";
import { CellContext, HeaderContext } from "@tanstack/react-table";
import React from "react";
import { getCorrelation, getTotalValueOfAllRows } from "./calculations";
type Props<T> = {
  compared?: boolean;
} & (
  | {
      info: CellContext<T, number | undefined>;
      calculateTotals?: false;
      total?: never;
      comparedTotal?: never;
    }
  | {
      info: HeaderContext<T, number | undefined>;
      calculateTotals: true;
      total?: never;
      comparedTotal?: never;
    }
  | {
      total: number;
      comparedTotal?: number;
      info?: never;
      calculateTotals?: never;
    }
);

export type CurrencyCalculatorType = (
  value: number | undefined,
  numDigits: number,
  displaySymbol?: boolean
) => string;

const getDisplayString = (
  value: number,
  currencyCalculator: CurrencyCalculatorType
) => {
  return currencyCalculator(value, 2);
};

function CpoTableDisplay<T extends Record<string, any>>({
  info,
  compared,
  calculateTotals,
  total,
  comparedTotal,
}: Props<T>) {
  const { calculateCurrencyConvertion } = useFormatContext();
  let comparedValue = 0;
  let value = 0;

  if (total !== undefined) {
    value = total;
    comparedValue = comparedTotal ?? 0;
  } else if (calculateTotals) {
    const allSpendValues = info.column.getFacetedRowModel().rows.map((row) => {
      return {
        value: row.original?.["spend"],
        compared: row.original?.compared?.["spend"],
      };
    });
    const allOrderValues = info.column.getFacetedRowModel().rows.map((row) => {
      return {
        value: row.original?.["purchaseCount"],
        compared: row.original?.compared?.["purchaseCount"],
      };
    });
    const spendTotals = getTotalValueOfAllRows({
      values: allSpendValues,
    });
    const orderTotals = getTotalValueOfAllRows({
      values: allOrderValues,
    });
    value =
      orderTotals.value && spendTotals.value
        ? spendTotals.value / orderTotals.value
        : 0;
    comparedValue =
      orderTotals.compared && spendTotals.compared
        ? spendTotals.compared / orderTotals.compared
        : 0;
  } else {
    value = info.getValue() ?? 0;
    if (compared && info.row.original?.compared) {
      comparedValue = info.row.original?.compared[info.column?.id];
    }
  }
  const rowHasValue = value || comparedValue;
  if (!rowHasValue) return "-";
  const currentValueEl = getDisplayString(value, calculateCurrencyConvertion);
  if (compared) {
    const rate = getCorrelation(value, comparedValue, 2);
    return (
      <div className="flex items-center w-full">
        <div className="flex-1">
          <p data-display="currency">{currentValueEl}</p>
          <p
            className="text-[11px] leading-tight opacity-70"
            data-display="currency"
            data-compared={true}
          >
            vs {getDisplayString(comparedValue, calculateCurrencyConvertion)}
          </p>
        </div>
        <RateDisplay rate={rate} isIncreasing={rate < 0} size="small" />
      </div>
    );
  }
  return <p data-display="currency">{currentValueEl}</p>;
}

export default CpoTableDisplay;
